import { FC, useState } from 'react'
import { ReactComponent as MetaLogo } from 'src/assets/svg/meta-ads.svg'
import { useAdPerfCampaigns } from 'src/reactQuery/useAdPerformance'
import { AdPerfRange } from 'src/services/adPerformanceApi'
import s from './dashboard.module.scss'
import { ChevronDown } from './icons'
import {
  actionResults,
  compact,
  money,
  objectiveLabel,
  pct,
  resultTypeLabel,
} from '../format'

const PAGE = 5

interface Props {
  currency: string
  range: AdPerfRange
}

export const Campaigns: FC<Props> = ({ currency, range }) => {
  const { data = [], isLoading } = useAdPerfCampaigns(range)
  const rows = data.filter((c) => c.spend > 0 || c.impressions > 0)
  const total = rows.reduce((a, c) => a + c.spend, 0)
  const [open, setOpen] = useState(true)
  const [showAll, setShowAll] = useState(false)
  const visible = showAll ? rows : rows.slice(0, PAGE)
  const sum = (k: 'clicks' | 'impressions') =>
    rows.reduce((a, c) => a + c[k], 0)
  const imp = sum('impressions')
  const clicks = sum('clicks')
  const results = rows.reduce((a, c) => a + actionResults(c), 0)
  // Score cards above the table, as in the design.
  const tiles = [
    { label: 'Impressions', value: compact(imp) },
    {
      label: 'Avg. CPM',
      value: money(imp ? (total / imp) * 1000 : null, currency, 2),
    },
    { label: 'Clicks', value: compact(clicks) },
    { label: 'Avg. CTR', value: pct(imp ? (clicks / imp) * 100 : null) },
    {
      label: 'Avg. CPC',
      value: money(clicks ? total / clicks : null, currency, 2),
    },
    { label: 'Results', value: compact(results) },
    { label: 'Total spend', value: money(total, currency) },
  ]

  return (
    <div className={s.card}>
      <div className={s.cardHeader}>
        <h3 className={s.cardTitle}>Campaigns</h3>
        <span className={s.subtle}>{rows.length} campaigns</span>
      </div>
      {rows.length > 0 && (
        <>
          <div className={s.campaignTiles}>
            {tiles.map((t) => (
              <div className={s.campaignTile} key={t.label}>
                <span>{t.label}</span>
                <strong>{t.value}</strong>
              </div>
            ))}
          </div>
          <div className={s.spendSplitTitle}>Spend by platform</div>
          <div className={s.spendSplit}>
            <span style={{ background: '#0866ff', width: '100%' }} />
          </div>
          <div className={s.legend} style={{ margin: '10px 0 24px' }}>
            <span>
              <i style={{ background: '#0866ff' }} />
              Meta {money(total, currency)} · 100%
            </span>
          </div>
        </>
      )}
      {!isLoading && !rows.length ? (
        <div className={s.empty}>No campaign delivered in this period.</div>
      ) : (
        <div className={s.tableWrap}>
          <table className={s.table}>
            <thead>
              <tr>
                <th>Campaign</th>
                <th>Impressions</th>
                <th>CPM</th>
                <th>Clicks</th>
                <th>CTR</th>
                <th>CPC</th>
                <th>Results</th>
                <th>Spend</th>
              </tr>
            </thead>
            <tbody>
              <tr className={s.groupRow} onClick={() => setOpen((v) => !v)}>
                <td>
                  <span className={s.groupCell}>
                    <ChevronDown
                      className={s.groupChevron}
                      style={open ? undefined : { transform: 'rotate(-90deg)' }}
                    />
                    <span className={s.platformDot}>
                      <MetaLogo />
                    </span>
                    Meta
                    <span className={s.countPill}>{rows.length} campaigns</span>
                  </span>
                </td>
                <td>{compact(imp)}</td>
                <td>{money(imp ? (total / imp) * 1000 : null, currency, 2)}</td>
                <td>{compact(clicks)}</td>
                <td>{pct(imp ? (clicks / imp) * 100 : null)}</td>
                <td>{money(clicks ? total / clicks : null, currency, 2)}</td>
                <td>{compact(results)}</td>
                <td>
                  {money(total, currency)}{' '}
                  <span className={s.small}>/ 100%</span>
                </td>
              </tr>
              {open &&
                visible.map((c) => (
                  <tr key={c.campaign_id}>
                    <td>
                      <div className={s.campaignName} title={c.name}>
                        {c.name}
                      </div>
                      <div className={s.small}>
                        {objectiveLabel(c.objective)}
                        {c.status ? ` · ${c.status.toLowerCase()}` : ''}
                      </div>
                    </td>
                    <td>{compact(c.impressions)}</td>
                    <td>{money(c.cpm, currency, 2)}</td>
                    <td>{compact(c.clicks)}</td>
                    <td>{pct(c.ctr)}</td>
                    <td>{money(c.cpc, currency, 2)}</td>
                    <td>
                      {c.results === null ? '–' : compact(c.results)}{' '}
                      <span className={s.small}>
                        {resultTypeLabel(c.result_type)}
                      </span>
                    </td>
                    <td>
                      {money(c.spend, currency)}{' '}
                      <span className={s.small}>/ {c.share_spend ?? 0}%</span>
                    </td>
                  </tr>
                ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={7}>
                  <span className={s.subtle}>
                    Showing {open ? visible.length : 0} of {rows.length}{' '}
                    campaigns
                  </span>
                  {open && rows.length > PAGE && (
                    <button
                      className={s.linkButton}
                      onClick={() => setShowAll((v) => !v)}
                      type="button"
                    >
                      {showAll ? 'Show less' : 'Show all'}
                    </button>
                  )}
                </td>
                <td>
                  <strong>{money(total, currency)} total</strong>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  )
}
